// components/common/charts/RevenueCharts.jsx - Revenue chart using VerticalStackedBarChart
import React, { useMemo } from "react";
import { VerticalStackedBarChart } from "../../common/VerticalStackedBarChart";
import { CHART_COLORS } from "../../../utils/chartUtils";

// Helper function to create full 12-month template
const createFullYearTemplate = (year) => {
  const months = [
    { num: 1, name: 'January', short: 'Jan' },
    { num: 2, name: 'February', short: 'Feb' },
    { num: 3, name: 'March', short: 'Mar' },
    { num: 4, name: 'April', short: 'Apr' },
    { num: 5, name: 'May', short: 'May' },
    { num: 6, name: 'June', short: 'Jun' },
    { num: 7, name: 'July', short: 'Jul' },
    { num: 8, name: 'August', short: 'Aug' },
    { num: 9, name: 'September', short: 'Sep' },
    { num: 10, name: 'October', short: 'Oct' },
    { num: 11, name: 'November', short: 'Nov' },
    { num: 12, name: 'December', short: 'Dec' },
  ];

  return months.map(month => ({
    label: `${month.short} ${year}`,
    month: month.num,
    month_num: month.num,
    month_name: month.name,
    year: year,
    retainer_revenue: 0,
    project_revenue: 0,
    other_revenue: 0,
    revenue: 0,
    last_year_revenue: 0,
    transaction_count: 0,
    has_previous_year_data: false,
    is_future: true,
  }));
};

/**
 * RevenueChart Component
 * Specialized wrapper around VerticalStackedBarChart for revenue data
 * Automatically handles 12-month display and revenue segmentation
 * 
 * @param {Array} data - Revenue data from API
 * @param {Object} dateRange - Date range object (optional)
 * @param {Object} summaryStats - Summary statistics (optional)
 */
export const RevenueChart = ({ data, dateRange, summaryStats }) => {
  // Process data to include full 12 months with revenue segments
  const chartData = useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }

    // Determine the year to display
    const dataYears = data.map(item => item.year).filter(Boolean);
    const displayYear = dataYears.length > 0 ? Math.max(...dataYears) : new Date().getFullYear();

    // Create full 12-month template
    const fullYearData = createFullYearTemplate(displayYear);

    // Merge actual data into the template
    const dataMap = new Map();
    data.forEach(item => {
      const monthNum = item.month_num || item.month;
      if (monthNum) {
        const monthShort = fullYearData[monthNum - 1].label.split(' ')[0];
        dataMap.set(monthNum, {
          label: item.month || `${monthShort} ${item.year || displayYear}`,
          month: monthNum,
          month_num: monthNum,
          month_name: item.month_name,
          year: item.year || displayYear,
          retainer_revenue: parseFloat(item.retainer_revenue || 0),
          project_revenue: parseFloat(item.project_revenue || 0),
          other_revenue: parseFloat(item.other_revenue || 0),
          revenue: parseFloat(item.revenue || 0),
          last_year_revenue: parseFloat(item.last_year_revenue || 0),
          transaction_count: item.transaction_count || 0,
          has_previous_year_data: (item.last_year_revenue || 0) > 0,
          is_future: item.revenue === 0 && item.last_year_revenue === 0,
        });
      }
    });

    // Merge data into template
    return fullYearData.map(templateMonth => {
      const actualData = dataMap.get(templateMonth.month_num);
      return actualData || templateMonth;
    });
  }, [data]);

  // Revenue-specific configuration
  const config = {
    segments: [
      {
        dataKey: "retainer_revenue",
        name: "Retainer",
        color: CHART_COLORS.retainer,
      },
      {
        dataKey: "project_revenue",
        name: "Project",
        color: CHART_COLORS.project,
      },
      {
        dataKey: "other_revenue",
        name: "Other Revenue",
        color: CHART_COLORS.other,
      },
    ],
    comparisonKey: "last_year_revenue",
    comparisonColor: CHART_COLORS.lastYear,
    showComparison: true,
    showGrowth: true,
    valueFormatter: (value) => `$${value >= 1000 ? `${(value / 1000).toFixed(1)}K` : value.toFixed(0)}`,
    comparisonLabel: "Last Year",
    comparisonName: "Last Year Total",
    labelKey: "label",
  };

  return (
    <VerticalStackedBarChart
      data={chartData}
      config={config}
      showLegend={true}
    />
  );
};

export default RevenueChart;